import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_clearout-pro/artifacts/y30mzl7k_Junk%20Removal%20Transparent%20Logo.webp";

const footerLinks = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'Reviews', path: '/reviews' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

const services = [
  'Furniture Removal',
  'Appliance Removal',
  'Home Cleanouts',
  'Debris Removal',
  'Yard Cleanup',
  'Demolition',
];

export default function Footer() {
  return (
    <footer className="bg-[#1a1a1a] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <img
              src={LOGO_URL}
              alt="Junk Removal M.D."
              className="h-16 w-auto"
              loading="lazy"
            />
            <p className="text-gray-400 text-sm leading-relaxed">
              Fast, insured, eco-friendly junk removal for homes and businesses across Northeast Oklahoma.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/JunkRemovalMD/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#1877f2] hover:bg-[#1664d9] rounded-full flex items-center justify-center transition-colors"
                aria-label="Facebook"
                data-testid="footer-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/junkremovalmd"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gradient-to-br from-[#833ab4] via-[#fd1d1d] to-[#fcb045] hover:opacity-90 rounded-full flex items-center justify-center transition-opacity"
                aria-label="Instagram"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wide mb-6">
              Quick Links
            </h4>
            <nav className="space-y-3">
              {footerLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block text-gray-400 hover:text-white transition-colors text-sm"
                  data-testid={`footer-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wide mb-6">
              Services
            </h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <Link
                    to="/services"
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wide mb-6">
              Contact Us
            </h4>
            <div className="space-y-4">
              <a
                href="tel:9183210654"
                className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors group"
                data-testid="footer-phone"
              >
                <div className="w-10 h-10 bg-[#dc2626] rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <span className="font-semibold">(918) 321-0654</span>
              </a>
              <a
                href="mailto:support@junkremovalmd.com"
                className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors"
                data-testid="footer-email"
              >
                <div className="w-10 h-10 bg-[#dc2626] rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <span className="text-sm">support@junkremovalmd.com</span>
              </a>
              <div className="flex items-center gap-3 text-gray-400">
                <div className="w-10 h-10 bg-[#dc2626] rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <span className="text-sm">Tahlequah, Oklahoma</span>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <div className="w-10 h-10 bg-[#dc2626] rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <span className="text-sm">8 A.M. – 8 P.M.</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Junk Removal M.D. All rights reserved.
            </p>
            <p className="text-gray-500 text-sm">
              Proudly serving Cherokee County & Northeast Oklahoma
            </p>
          </div>
        </div>
      </div>

      {/* LocalBusiness Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Junk Removal M.D.",
          "image": "https://customer-assets.emergentagent.com/job_clearout-pro/artifacts/px7wjijn_Junk%20Removal%20Logo.webp",
          "telephone": "(918) 321-0654",
          "email": "support@junkremovalmd.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Tahlequah",
            "addressRegion": "OK",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 35.9149,
            "longitude": -94.9699
          },
          "url": "https://junkremovalmd.com",
          "openingHours": "Mo-Su 08:00-20:00",
          "priceRange": "$$",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "5",
            "reviewCount": "85"
          },
          "areaServed": [
            "Cherokee County, OK",
            "Adair County, OK",
            "Mayes County, OK",
            "Delaware County, OK",
            "Craig County, OK",
            "Ottawa County, OK",
            "Wagoner County, OK",
            "Muskogee County, OK",
            "Sequoyah County, OK"
          ],
          "serviceType": [
            "Junk Removal",
            "Furniture Removal",
            "Appliance Removal",
            "Home Cleanouts",
            "Debris Removal",
            "Demolition"
          ]
        })
      }} />
    </footer>
  );
}
